import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Upload, Users, FileSpreadsheet, Send, PauseCircle, PlayCircle, XCircle, CheckCircle2, Clock, AlertTriangle, Mail } from "lucide-react";

export default function AdminMembers() {
  const [tab, setTab] = useState("list");

  return (
    <div className="space-y-4" data-testid="admin-members">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl sm:text-3xl font-display font-semibold">الأعضاء</h1>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="members-tabs">
          <TabsTrigger value="list" data-testid="tab-list"><Users className="h-4 w-4 me-1.5" /> قائمة الأعضاء</TabsTrigger>
          <TabsTrigger value="import" data-testid="tab-import"><FileSpreadsheet className="h-4 w-4 me-1.5" /> استيراد من Excel</TabsTrigger>
          <TabsTrigger value="batches" data-testid="tab-batches"><Send className="h-4 w-4 me-1.5" /> دفعات الاستيراد</TabsTrigger>
        </TabsList>
        <TabsContent value="list"><MembersList /></TabsContent>
        <TabsContent value="import"><ImportMembers onDone={() => setTab("batches")} /></TabsContent>
        <TabsContent value="batches"><ImportBatches /></TabsContent>
      </Tabs>
    </div>
  );
}

// ---------------- Members List ----------------
function MembersList() {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  const [accessType, setAccessType] = useState("any");
  const [changeEmailFor, setChangeEmailFor] = useState(null); // member obj or null
  const load = () => api.get("/admin/members", { params: { q: q || undefined, access_type: accessType === "any" ? undefined : accessType } }).then((r) => setMembers(r.data));
  useEffect(() => { load(); }, [accessType]);

  const suspend = async (id) => { await api.post(`/admin/members/${id}/suspend`); toast.success("تم التعليق"); load(); };
  const activate = async (id) => { await api.post(`/admin/members/${id}/activate`); toast.success("تم التفعيل"); load(); };
  const cancelSub = async (m) => {
    // Kept for backwards-compat / other callers; the Members page cancel button was
    // removed in iter42 — cancel is now only available from /admin/subscriptions.
    if (!confirm(`إلغاء اشتراك ${m.full_name}؟\nسيبقى الوصول ساريًا حتى نهاية الفترة الحالية، ثم يُلغى تلقائيًا.`)) return;
    try {
      await api.post(`/admin/members/${m.id}/cancel-subscription`);
      toast.success("تم جدولة الإلغاء عند نهاية الفترة الحالية");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الإلغاء");
    }
  };

  const deleteMember = async (m) => {
    const confirm1 = confirm(
      `⚠️ حذف العميل ${m.full_name || m.email} نهائيًا؟\n\n` +
      "سيتم:\n" +
      "• إخفاء الحساب من جميع الشاشات\n" +
      "• إخفاء بريده وهاتفه واسمه (استبدال بمعرّف مجهول)\n" +
      "• منعه من تسجيل الدخول\n" +
      "• الاحتفاظ بسجلات الشراء للمحاسبة (بدون بياناته الشخصية)\n\n" +
      "لا يمكن التراجع عن هذا الإجراء."
    );
    if (!confirm1) return;
    const typed = prompt(`للتأكيد، اكتب: DELETE`);
    if (typed !== "DELETE") { toast.error("تم إلغاء الحذف"); return; }
    try {
      await api.delete(`/admin/members/${m.id}`);
      toast.success("تم حذف العميل بأمان (مع الحفاظ على سجلات المحاسبة)");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل الحذف");
    }
  };

  const setPassword = async (m) => {
    if (!confirm(
      `توليد كلمة مرور مؤقتة للعضو ${m.full_name || m.email}؟\n\n` +
      "سيتم استبدال كلمة المرور الحالية. أعطِ العميل البريد + كلمة المرور المؤقتة عبر واتساب. " +
      "يمكن للعميل تغييرها لاحقًا من إعدادات الحساب."
    )) return;
    try {
      const { data } = await api.post(`/admin/members/${m.id}/set-password`);
      // Copy credentials to clipboard for admin to paste in WhatsApp
      const text = `البريد: ${data.email}\nكلمة المرور المؤقتة: ${data.temporary_password}\n\nيمكنك تسجيل الدخول من: https://faisalfit.com/login`;
      await navigator.clipboard.writeText(text);
      toast.success(
        `تم إنشاء كلمة مرور مؤقتة ونسخها للحافظة: ${data.temporary_password}`,
        { duration: 20000 },
      );
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل إنشاء كلمة المرور");
    }
  };

  const subBadge = (m) => {
    const s = m.subscription_status;
    if (!s || s === "none") return <span className="text-[10px] text-foreground/40">—</span>;
    const styles = {
      active: "bg-green-100 text-green-800",
      canceling: "bg-amber-100 text-amber-900",
      past_due: "bg-red-100 text-red-800",
      canceled: "bg-slate-100 text-slate-600",
    };
    const labels = {
      active: "مشترك",
      canceling: "سيُلغى",
      past_due: "متأخر",
      canceled: "ملغى",
    };
    return <Badge className={`text-[10px] ${styles[s] || "bg-slate-100"}`}>{labels[s] || s}</Badge>;
  };

  const accessBadge = (m) => {
    const a = m.access_type || "unknown";
    const styles = {
      paid_subscription: "bg-emerald-100 text-emerald-800",
      lifetime: "bg-violet-100 text-violet-800",
      free_forever: "bg-sky-100 text-sky-800",
      legacy: "bg-slate-100 text-slate-600",
      unknown: "bg-slate-50 text-slate-400",
    };
    const labels = {
      paid_subscription: "مدفوع",
      lifetime: "مدى الحياة",
      free_forever: "مجاني للأبد",
      legacy: "قديم (دفعة/قسط)",
      unknown: "غير محدد",
    };
    return <Badge className={`text-[10px] ${styles[a]}`}>{labels[a] || a}</Badge>;
  };

  return (
    <div className="space-y-4 pt-2">
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-2 flex-wrap max-w-3xl">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم أو البريد أو الهاتف..." className="bg-white border-foreground/10 text-foreground flex-1 min-w-[240px]" data-testid="members-search" />
        <select
          value={accessType}
          onChange={(e) => setAccessType(e.target.value)}
          className="border rounded-md px-2 py-1.5 text-xs bg-white"
          data-testid="members-access-filter"
        >
          <option value="any">جميع الأنواع</option>
          <option value="paid_subscription">مشتركون مدفوعون</option>
          <option value="lifetime">مدى الحياة</option>
          <option value="free_forever">مجاني للأبد</option>
          <option value="legacy">قديم (دفعة/قسط)</option>
          <option value="unknown">غير محدد</option>
        </select>
        <Button type="submit" data-testid="members-search-submit">بحث</Button>
      </form>
      <Card className="bg-white border-foreground/10">
        <Table>
          <TableHeader>
            <TableRow className="border-foreground/10 hover:bg-transparent">
              <TableHead className="text-foreground">الاسم</TableHead>
              <TableHead className="text-foreground">البريد</TableHead>
              <TableHead className="text-foreground">الهاتف</TableHead>
              <TableHead className="text-foreground">النوع</TableHead>
              <TableHead className="text-foreground">الحالة</TableHead>
              <TableHead className="text-foreground">الاشتراك</TableHead>
              <TableHead className="text-foreground">الدورات</TableHead>
              <TableHead className="text-foreground">إجراءات</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {members.map((m) => (
              <TableRow key={m.id} className="border-foreground/10 hover:bg-foreground/5" data-testid={`member-row-${m.id}`}>
                <TableCell>{m.full_name}</TableCell>
                <TableCell className="font-mono text-xs">{m.email}</TableCell>
                <TableCell className="text-xs">{m.phone || "—"}</TableCell>
                <TableCell>{accessBadge(m)}</TableCell>
                <TableCell>
                  <Badge variant={m.status === "active" ? "default" : "secondary"}>
                    {m.status === "active" ? "نشط" : m.status === "suspended" ? "معلق" : m.status}
                  </Badge>
                </TableCell>
                <TableCell>{subBadge(m)}</TableCell>
                <TableCell>{m.enrollments_count}</TableCell>
                <TableCell>
                  <div className="flex gap-1 flex-wrap">
                    {m.status === "active" ? (
                      <Button size="sm" variant="destructive" onClick={() => suspend(m.id)} data-testid={`btn-suspend-${m.id}`} title="حظر تسجيل الدخول دون حذف البيانات (يمكن التراجع)">تعليق</Button>
                    ) : m.status === "deleted" ? (
                      <span className="text-[10px] text-foreground/40">محذوف</span>
                    ) : (
                      <Button size="sm" onClick={() => activate(m.id)} data-testid={`btn-activate-${m.id}`} title="إلغاء التعليق واستعادة الدخول">تفعيل</Button>
                    )}
                    {m.status !== "deleted" && (
                      <>
                        <Button size="sm" variant="outline" onClick={() => setChangeEmailFor(m)} data-testid={`btn-change-email-${m.id}`} title="تعديل البريد الإلكتروني وتحديث Stripe">
                          <Mail className="h-3.5 w-3.5 me-1" /> البريد
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setPassword(m)} data-testid={`btn-set-pw-${m.id}`} title="توليد كلمة مرور مؤقتة (تُنسخ للحافظة)">
                          كلمة مرور
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteMember(m)} data-testid={`btn-delete-${m.id}`} title="حذف نهائي مع الحفاظ على سجلات الشراء (لا يمكن التراجع)" className="text-red-700 hover:bg-red-50">
                          حذف
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {members.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center opacity-60 py-8">لا أعضاء</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <ChangeEmailDialog
        member={changeEmailFor}
        onClose={() => setChangeEmailFor(null)}
        onDone={() => { setChangeEmailFor(null); load(); }}
      />
    </div>
  );
}

// ---------------- Change Email Dialog ----------------
function ChangeEmailDialog({ member, onClose, onDone }) {
  const [newEmail, setNewEmail] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    // Reset state whenever the dialog is opened for a new member
    if (member) { setNewEmail(""); setReason(""); setResult(null); }
  }, [member?.id]);

  if (!member) return null;

  const submit = async () => {
    const trimmed = (newEmail || "").trim().toLowerCase();
    if (!trimmed || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(trimmed)) {
      toast.error("أدخل بريداً إلكترونياً صحيحاً");
      return;
    }
    if (trimmed === (member.email || "").toLowerCase()) {
      toast.error("البريد الجديد مطابق للبريد الحالي");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/members/${member.id}/change-email`, {
        new_email: trimmed,
        reason: reason.trim() || undefined,
      });
      setResult(data);
      toast.success("تم تحديث البريد بنجاح");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "فشل تحديث البريد");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!member} onOpenChange={(v) => !v && onClose()}>
      <DialogContent dir="rtl" className="max-w-lg" data-testid="change-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" /> تعديل البريد الإلكتروني
          </DialogTitle>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="rounded-lg bg-secondary/60 p-3 text-xs space-y-1">
              <div><span className="text-muted-foreground">العضو:</span> <b>{member.full_name || "—"}</b></div>
              <div><span className="text-muted-foreground">البريد الحالي:</span> <b dir="ltr" className="inline-block">{member.email}</b></div>
            </div>

            <div className="rounded-lg border border-primary/25 bg-primary/5 p-3 text-xs space-y-1">
              <p className="font-medium">ماذا سيحصل عند الحفظ؟</p>
              <ul className="list-disc ps-5 text-muted-foreground space-y-0.5">
                <li>تغيير بريد تسجيل الدخول للعضو.</li>
                <li>تحديث جميع سجلات الشراء المرتبطة بالبريد القديم.</li>
                <li>تحديث بريد العميل في Stripe لتصله الفواتير والإشعارات على البريد الجديد.</li>
                <li>الاشتراك سيستمر بالتجديد تلقائياً على نفس البطاقة — لا حاجة لإعادة الدفع.</li>
              </ul>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">البريد الجديد <span className="text-red-500">*</span></label>
              <Input
                type="email"
                dir="ltr"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="new-email@example.com"
                autoFocus
                data-testid="change-email-new"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">سبب التعديل <span className="text-muted-foreground text-xs">(اختياري — للسجل)</span></label>
              <Input
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="مثال: العميل تواصل عبر واتساب وذكر أنه كتب بريده بشكل خاطئ"
                data-testid="change-email-reason"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={onClose} disabled={busy}>إلغاء</Button>
              <Button onClick={submit} disabled={busy || !newEmail.trim()} data-testid="btn-change-email-submit">
                {busy ? "جارٍ الحفظ..." : "حفظ التعديل"}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="change-email-result">
            <div className="rounded-lg border-2 border-emerald-500/40 bg-emerald-50 dark:bg-emerald-950/30 p-4 text-sm space-y-2">
              <div className="flex items-center gap-2 font-medium text-emerald-900 dark:text-emerald-100">
                <CheckCircle2 className="h-4 w-4" /> تم التحديث
              </div>
              <div className="text-xs text-emerald-900/80 dark:text-emerald-100/80 space-y-0.5">
                <div>البريد القديم: <b dir="ltr">{result.old_email}</b></div>
                <div>البريد الجديد: <b dir="ltr">{result.new_email}</b></div>
                <div>سجلات شراء محدَّثة: <b>{result.purchases_updated}</b></div>
                <div>عملاء Stripe محدَّثون: <b>{result.stripe_customers_updated}</b></div>
                {result.stripe_error && (
                  <div className="text-amber-700 dark:text-amber-300 mt-1">
                    ⚠ Stripe أعاد خطأً على أحد العملاء: <span dir="ltr">{result.stripe_error}</span>
                    <div className="text-[10px] mt-1">التحديث المحلي تم بنجاح — يمكنك محاولة تعديل بريد Stripe يدوياً من لوحة Stripe.</div>
                  </div>
                )}
              </div>
              <p className="text-xs text-emerald-900/70 dark:text-emerald-100/70 pt-1">{result.message}</p>
            </div>
            <div className="flex justify-end">
              <Button onClick={onDone} data-testid="btn-change-email-close">إغلاق</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Import Members (Step wizard) ----------------
function ImportMembers({ onDone }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableCourses, setAvailableCourses] = useState([]);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [dailyRate, setDailyRate] = useState(50);
  const [batchLabel, setBatchLabel] = useState("");
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/courses/admin/list").then((r) => setAvailableCourses(r.data || []));
  }, []);

  const handleFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    setPreview(null);
    setResult(null);
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/members-import/preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error("فشل قراءة الملف: " + (err?.response?.data?.detail || "خطأ"));
      setFile(null);
    } finally {
      setLoading(false);
    }
  };

  const [accessTypeImport, setAccessTypeImport] = useState("free_forever");

  const toggleCourse = (id) => {
    setSelectedCourseIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const commit = async () => {
    if (!file) return toast.error("اختر ملفًا أولًا");
    // For free_forever with all-courses default, empty course_ids is allowed (backend expands to all published)
    if (selectedCourseIds.length === 0 && accessTypeImport !== "free_forever") return toast.error("اختر دورة واحدة على الأقل");
    if (!preview || preview.will_import === 0) return toast.error("لا يوجد أعضاء صالحون للاستيراد");
    if (!confirm(`سيتم إنشاء ${preview.will_import} حسابًا كـ«${accessTypeImport === 'free_forever' ? 'مجاني للأبد' : 'قديم'}» وإرسال ${dailyRate} رسالة يوميًا. متابعة؟`)) return;
    setCommitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("course_ids", selectedCourseIds.join(","));
      fd.append("daily_rate", String(dailyRate));
      fd.append("access_type", accessTypeImport);
      if (batchLabel) fd.append("batch_label", batchLabel);
      const { data } = await api.post("/admin/members-import/commit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(`تم إنشاء ${data.invites_created} دعوة وجدولة ${data.emails_queued} رسالة`);
    } catch (err) {
      toast.error("فشل الاستيراد: " + (err?.response?.data?.detail || "خطأ"));
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="space-y-4 pt-2">
      {/* Step 1: File Upload */}
      <Card className="p-4 space-y-3">
        <div className="flex items-center gap-2 font-semibold">
          <span className="h-6 w-6 rounded-full bg-black text-white text-xs grid place-items-center">١</span>
          <span>ارفع ملف Excel أو CSV</span>
        </div>
        <p className="text-xs text-foreground/60">
          يجب أن يحتوي الملف على الأعمدة التالية (أي عمود إضافي يُتجاهل):
          <br />
          <strong>email</strong> (إلزامي) — <strong>full_name / الاسم</strong> — <strong>whatsapp / phone / الجوال</strong>
        </p>
        <label className="inline-flex">
          <input type="file" accept=".csv,.xlsx" className="hidden" onChange={handleFileChange} data-testid="import-members-file" />
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-md border cursor-pointer hover:bg-foreground/5">
            <Upload className="h-4 w-4" /> {file ? file.name : "اختر الملف"}
          </span>
        </label>
        {loading && <p className="text-xs text-foreground/60">جاري تحليل الملف...</p>}
      </Card>

      {/* Preview */}
      {preview && (
        <Card className="p-4 space-y-3" data-testid="import-preview">
          <div className="font-semibold flex items-center gap-2">
            <span className="h-6 w-6 rounded-full bg-black text-white text-xs grid place-items-center">٢</span>
            معاينة الاستيراد
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <MiniStat label="إجمالي الصفوف" value={preview.total_rows} />
            <MiniStat label="سيُستورد" value={preview.will_import} color="text-green-700" />
            <MiniStat label="مسجل مسبقًا" value={preview.skipped_already_member} color="text-blue-700" />
            <MiniStat label="لديه دعوة معلقة" value={preview.skipped_pending_invite} color="text-yellow-700" />
          </div>
          {preview.skipped_invalid_or_duplicate_in_file > 0 && (
            <p className="text-xs text-red-700 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> {preview.skipped_invalid_or_duplicate_in_file} صف غير صالح أو مكرر في الملف
            </p>
          )}
          {preview.sample.length > 0 && (
            <div className="text-xs">
              <div className="text-foreground/60 mb-1">أول {preview.sample.length} أعضاء:</div>
              <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                {preview.sample.map((r, i) => (
                  <div key={i} className="p-2 flex justify-between gap-3">
                    <span className="font-mono">{r.email}</span>
                    <span className="text-foreground/60 truncate">{r.full_name}</span>
                    <span dir="ltr" className="text-foreground/50 text-[10px]">{r.phone}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Step 2: Configure */}
      {preview && preview.will_import > 0 && (
        <Card className="p-4 space-y-4" data-testid="import-config">
          <div className="font-semibold flex items-center gap-2">
            <span className="h-6 w-6 rounded-full bg-black text-white text-xs grid place-items-center">٣</span>
            اختر الدورات وسرعة الإرسال
          </div>

          {/* Courses */}
          <div>
            <label className="text-sm font-medium block mb-2">الدورات التي سيُمنحون الوصول إليها</label>
            <div className="grid gap-2 sm:grid-cols-2">
              {availableCourses.map((c) => (
                <label key={c.id} className={`border rounded-md p-3 cursor-pointer flex items-center gap-2 ${selectedCourseIds.includes(c.id) ? "border-foreground bg-foreground/5" : "border-foreground/10 bg-white"}`}>
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={selectedCourseIds.includes(c.id)}
                    onChange={() => toggleCourse(c.id)}
                    data-testid={`course-check-${c.id}`}
                  />
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.title}</div>
                    <div className="text-[10px] text-foreground/50 truncate">{c.subtitle || ""}</div>
                  </div>
                </label>
              ))}
              {availableCourses.length === 0 && <p className="text-xs text-foreground/50">لا توجد دورات — أنشئ دورة أولًا.</p>}
            </div>
          </div>

          {/* Access type */}
          <div>
            <label className="text-sm font-medium block mb-2">نوع الوصول للمستوردين</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {[
                { v: "free_forever", label: "مجاني للأبد", hint: "لا يُحتَسبون في تحليلات الاشتراكات" },
                { v: "legacy", label: "قديم (دفعة/قسط)", hint: "عملاء دفعوا قبل نظام الاشتراك" },
              ].map((opt) => (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setAccessTypeImport(opt.v)}
                  className={`px-4 py-1.5 rounded-full text-sm border transition ${accessTypeImport === opt.v ? "bg-black text-white border-foreground" : "bg-white text-foreground border-foreground/20 hover:bg-foreground/5"}`}
                  data-testid={`import-access-${opt.v}`}
                  title={opt.hint}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-foreground/60">
              {accessTypeImport === "free_forever"
                ? "✨ إذا تركت اختيار الدورات فارغًا سيتم منح وصول لجميع الدورات المنشورة تلقائيًا."
                : "الأعضاء «القديمون» يظهرون في قائمة الأعضاء ولا يظهرون في تحليلات الاشتراكات."}
            </p>
          </div>

          {/* Daily rate */}
          <div>
            <label className="text-sm font-medium block mb-2">عدد الرسائل المُرسلة يوميًا</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {[25, 50, 100, 200].map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setDailyRate(n)}
                  className={`px-4 py-1.5 rounded-full text-sm border transition ${dailyRate === n ? "bg-black text-white border-foreground" : "bg-white text-foreground border-foreground/20 hover:bg-foreground/5"}`}
                  data-testid={`rate-${n}`}
                >
                  {n} / يوم
                </button>
              ))}
              <Input
                type="number" min={1} max={1000}
                value={dailyRate}
                onChange={(e) => setDailyRate(Math.max(1, parseInt(e.target.value) || 1))}
                className="w-24"
                data-testid="rate-custom"
              />
            </div>
            <p className="text-xs text-foreground/60">
              ⏱️ سيستغرق الإرسال حوالي <strong>{Math.ceil(preview.will_import / dailyRate)}</strong> يومًا لإكمال {preview.will_import} رسالة.
              <br />
              يُوصى بـ 50 يوميًا لبريد جديد لحماية سمعة النطاق من فلاتر السبام.
            </p>
          </div>

          {/* Label */}
          <div>
            <label className="text-sm font-medium block mb-1">اسم الدفعة (اختياري)</label>
            <Input
              value={batchLabel}
              onChange={(e) => setBatchLabel(e.target.value)}
              placeholder="مثال: أعضاء منصة سابقة - مارس"
              data-testid="import-label"
            />
          </div>

          <Button
            onClick={commit}
            disabled={committing || selectedCourseIds.length === 0}
            className="w-full sm:w-auto"
            data-testid="import-commit-btn"
          >
            {committing ? "جاري الاستيراد..." : `تأكيد الاستيراد وبدء الإرسال`}
          </Button>
        </Card>
      )}

      {/* Result */}
      {result && (
        <Card className="p-4 space-y-2 bg-green-50 border-green-200" data-testid="import-result">
          <div className="flex items-center gap-2 font-semibold text-green-900">
            <CheckCircle2 className="h-5 w-5" /> اكتمل الاستيراد بنجاح
          </div>
          <div className="text-sm space-y-1">
            <div>✅ {result.invites_created} دعوة تم إنشاؤها</div>
            <div>📧 {result.emails_queued} رسالة مجدولة للإرسال</div>
            <div>⏱️ ستكتمل خلال حوالي {result.estimated_days} يومًا</div>
          </div>
          <Button variant="outline" onClick={onDone} data-testid="goto-batches">عرض تفاصيل الدفعة</Button>
        </Card>
      )}
    </div>
  );
}

// ---------------- Batches ----------------
function ImportBatches() {
  const [batches, setBatches] = useState([]);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = () => api.get("/admin/members-import/batches").then((r) => setBatches(r.data));
  useEffect(() => { load(); }, []);

  const openBatch = async (b) => {
    setSelected(b);
    const { data } = await api.get(`/admin/members-import/batches/${b.id}`);
    setDetail(data);
  };

  const doPause = async () => { await api.post(`/admin/members-import/batches/${selected.id}/pause`); toast.success("تم الإيقاف"); openBatch(selected); load(); };
  const doResume = async () => { await api.post(`/admin/members-import/batches/${selected.id}/resume`); toast.success("تم الاستئناف"); openBatch(selected); load(); };
  const doCancel = async () => {
    if (!confirm("سيتم إلغاء جميع الرسائل والدعوات المعلقة. متابعة؟")) return;
    await api.post(`/admin/members-import/batches/${selected.id}/cancel`); toast.success("تم الإلغاء");
    openBatch(selected); load();
  };

  const dateShort = (iso) => iso ? new Date(iso).toLocaleString("ar", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <div className="space-y-3 pt-2">
      {batches.length === 0 && <Card className="p-8 text-center text-foreground/50">لا توجد دفعات استيراد حتى الآن.</Card>}
      {batches.map((b) => {
        const total = (b.sent_count || 0) + (b.pending_count || 0) + (b.failed_count || 0);
        const pct = total ? Math.round(((b.sent_count || 0) / total) * 100) : 0;
        return (
          <Card key={b.id} className="p-4 cursor-pointer hover:bg-black/[0.02]" onClick={() => openBatch(b)} data-testid={`batch-${b.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-semibold">{b.label}</div>
                <div className="text-xs text-foreground/60">{b.filename} • {b.daily_rate} / يوم • {dateShort(b.created_at)}</div>
                <div className="text-xs text-foreground/60 mt-1">دورات: {b.course_titles?.join(" • ")}</div>
              </div>
              <div className="text-left text-xs space-y-1">
                <div className="flex gap-2">
                  <span className="text-green-700"><CheckCircle2 className="h-3 w-3 inline" /> {b.sent_count || 0} أُرسلت</span>
                  <span className="text-yellow-700"><Clock className="h-3 w-3 inline" /> {b.pending_count || 0} معلقة</span>
                  {b.failed_count > 0 && <span className="text-red-700">{b.failed_count} فشل</span>}
                </div>
                <div>👤 {b.activated_count || 0} فعّلوا حساباتهم</div>
              </div>
            </div>
            <div className="mt-3 h-2 bg-foreground/10 rounded-full overflow-hidden">
              <div className="h-full bg-green-600 transition-all" style={{ width: `${pct}%` }} />
            </div>
          </Card>
        );
      })}

      <Dialog open={!!selected} onOpenChange={(o) => !o && (setSelected(null), setDetail(null))}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir="rtl">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.batch.label}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm">
                  <MiniStat label="إجمالي" value={detail.counts.total} />
                  <MiniStat label="أُرسلت" value={detail.counts.sent} color="text-green-700" />
                  <MiniStat label="معلقة" value={detail.counts.pending} color="text-yellow-700" />
                  <MiniStat label="فعّلوا" value={detail.counts.activated} color="text-blue-700" />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="outline" onClick={doPause} data-testid="batch-pause"><PauseCircle className="h-4 w-4 me-1" /> إيقاف</Button>
                  <Button size="sm" variant="outline" onClick={doResume} data-testid="batch-resume"><PlayCircle className="h-4 w-4 me-1" /> استئناف</Button>
                  <Button size="sm" variant="destructive" onClick={doCancel} data-testid="batch-cancel"><XCircle className="h-4 w-4 me-1" /> إلغاء الباقي</Button>
                </div>
                <div className="text-xs">
                  <div className="text-foreground/60 mb-1">أول 50 رسالة (من {detail.counts.total}):</div>
                  <div className="border rounded-md divide-y max-h-80 overflow-y-auto">
                    {detail.emails.slice(0, 50).map((e, i) => (
                      <div key={i} className="p-2 flex justify-between gap-3">
                        <span className="font-mono truncate flex-1 min-w-0">{e.to}</span>
                        <span className="text-foreground/50 text-[10px]">{dateShort(e.scheduled_at)}</span>
                        <Badge variant="secondary" className="text-[10px]">{e.status}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniStat({ label, value, color = "text-foreground" }) {
  return (
    <div className="border rounded-md p-3 bg-white">
      <div className="text-[10px] text-foreground/60">{label}</div>
      <div className={`text-xl font-semibold ${color}`}>{value}</div>
    </div>
  );
}
